/**
 * @module useAuth
 * @description React Query hooks for authentication operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { API_ENDPOINTS } from '../config/api.config.js';

/**
 * Hook to get available roles
 * @returns {Object} Query result with roles data
 */
export const useRoles = () => {
  return useQuery({
    queryKey: ['auth', 'roles'],
    queryFn: async () => {
      const response = await api.get(`${API_ENDPOINTS.AUTH}/roles`);
      return response.data;
    },
  });
};

/**
 * Hook to register a new user
 * @returns {Object} Mutation object for user registration
 */
export const useRegister = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (registrationData) => {
      const response = await api.post(`${API_ENDPOINTS.AUTH}/register`, registrationData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
};

/**
 * Hook to get user profile
 * @returns {Object} Query result with user profile data
 */
export const useProfile = () => {
  return useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AUTH}/profile`);
      return data;
    },
  });
};

/**
 * Hook to get current user (me endpoint)
 * @returns {Object} Query result with current user data
 */
export const useCurrentUser = () => {
  return useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      try {
        const response = await api.get(`${API_ENDPOINTS.AUTH}/me`);
        if (response.status === 200 && response.data) {
          return response.data;
        }
        return { role: 'viewer', permissions: [] };
      } catch {
        return { role: 'viewer', permissions: [] };
      }
    },
  });
};

/**
 * Hook to update user profile
 * @returns {Object} Mutation result for updating profile
 */
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (profileData) => {
      const { data } = await api.put(`${API_ENDPOINTS.AUTH}/profile`, profileData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
    },
  });
};

/**
 * Hook to login user
 * @returns {Object} Mutation result for login
 */
export const useLogin = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ username, password, role }) => {
      const { data } = await api.post(`${API_ENDPOINTS.AUTH}/login`, { username, password, role });
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
};

/**
 * Hook to get system features
 * @returns {Object} Query result with system features data
 */
export const useSystemFeatures = () => {
  return useQuery({
    queryKey: ['auth', 'system-features'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AUTH}/system-features`);
      return Array.isArray(data) ? data : (data?.features || []);
    },
  });
};

/**
 * Hook to get users simple list
 * @returns {Object} Query result with users simple data
 */
export const useUsersSimple = () => {
  return useQuery({
    queryKey: ['auth', 'users-simple'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AUTH}/users-simple`);
      return Array.isArray(data) ? data : [];
    },
  });
};

/**
 * Hook to get auth statistics
 * @returns {Object} Query result with auth stats data
 */
export const useAuthStats = () => {
  return useQuery({
    queryKey: ['auth', 'stats'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AUTH}/stats`);
      return data || {};
    },
  });
};

/**
 * Hook to get user activities
 * @returns {Object} Query result with user activities data
 */
export const useUserActivities = () => {
  return useQuery({
    queryKey: ['auth', 'user-activities'],
    queryFn: async () => {
      const { data } = await api.get(`${API_ENDPOINTS.AUTH}/user-activities`);
      return Array.isArray(data) ? data : (data?.activities || []);
    },
  });
};
